import { useState } from "react"
import { Check, X, RotateCcw, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import { OffboardingStatus, isOffboardingStatus, isOffboardingTerminal, isOffboardingResettable } from "@/modules/offboarding/enums"

const DEFAULT_GRACE_PERIOD_DAYS = 3

interface Props {
  offboardingId: string
  organizationId: string
  resignationStatus: string
  onRefresh: () => void
}

export function OffboardingApprovalActions({ offboardingId, organizationId, resignationStatus, onRefresh }: Props) {
  const [showAcceptDialog, setShowAcceptDialog] = useState(false)
  const [gracePeriodDays, setGracePeriodDays] = useState(String(DEFAULT_GRACE_PERIOD_DAYS))
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectComment, setRejectComment] = useState("")
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const parsedGracePeriodDays = Number(gracePeriodDays)
  const isGracePeriodValid = Number.isInteger(parsedGracePeriodDays) && parsedGracePeriodDays >= 0

  const handleAccept = async () => {
    if (!isGracePeriodValid) {
      toast.error("Please enter a valid number of days")
      return
    }
    setIsAccepting(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, {
        resignationStatus: OffboardingStatus.Accepted,
        gracePeriodDays: parsedGracePeriodDays,
      })
      toast.success("Offboarding accepted")
      setShowAcceptDialog(false)
      onRefresh()
    } catch {
      toast.error("Failed to accept offboarding")
    } finally {
      setIsAccepting(false)
    }
  }

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error("Please provide a comment")
      return
    }
    setIsRejecting(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, {
        resignationStatus: OffboardingStatus.Rejected,
        comments: rejectComment.trim(),
      })
      toast.success("Offboarding rejected")
      setShowRejectDialog(false)
      setRejectComment("")
      onRefresh()
    } catch {
      toast.error("Failed to reject offboarding")
    } finally {
      setIsRejecting(false)
    }
  }

  const handleReset = async () => {
    setIsResetting(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, {
        resignationStatus: OffboardingStatus.Pending,
      })
      toast.success("Offboarding reset — pending review again")
      onRefresh()
    } catch {
      toast.error("Failed to reset offboarding")
    } finally {
      setIsResetting(false)
    }
  }

  if (isOffboardingResettable(resignationStatus)) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={handleReset}
        disabled={isResetting}
      >
        {isResetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        Reset to Pending
      </Button>
    )
  }

  if (isOffboardingTerminal(resignationStatus)) return null

  return (
    <>
      <div className="flex gap-2">
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
          onClick={() => setShowAcceptDialog(true)}
          disabled={isAccepting}
        >
          <Check className="w-4 h-4" />
          Accept
        </Button>
        {isOffboardingStatus(resignationStatus, OffboardingStatus.Pending) && (
          <Button
            size="sm"
            variant="destructive"
            className="bg-red-600 hover:bg-red-700 gap-1.5"
            onClick={() => setShowRejectDialog(true)}
            disabled={isAccepting}
          >
            <X className="w-4 h-4" />
            Reject
          </Button>
        )}
      </div>

      <Dialog open={showAcceptDialog} onOpenChange={(open) => {
        if (!isAccepting) {
          setShowAcceptDialog(open)
          if (!open) setGracePeriodDays(String(DEFAULT_GRACE_PERIOD_DAYS))
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Accept Offboarding</DialogTitle>
            <DialogDescription>
              This cannot be undone. The employee keeps access until the grace period ends, then their account is terminated automatically.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium text-foreground">Grace period (days)</label>
            <Input
              className="mt-1"
              type="number"
              min={0}
              value={gracePeriodDays}
              onChange={(e) => setGracePeriodDays(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)} disabled={isAccepting}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
              onClick={handleAccept}
              disabled={isAccepting || !isGracePeriodValid}
            >
              {isAccepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Confirm Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRejectDialog} onOpenChange={(open) => {
        if (!isRejecting) {
          setShowRejectDialog(open)
          if (!open) setRejectComment("")
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Offboarding</DialogTitle>
            <DialogDescription>Please provide a reason for rejection.</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter reason for rejection..."
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            className="resize-none"
            rows={4}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => { setShowRejectDialog(false); setRejectComment("") }}
              disabled={isRejecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 gap-1.5"
              onClick={handleReject}
              disabled={isRejecting || !rejectComment.trim()}
            >
              {isRejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
